import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

// redux
import { useAppDispatch } from 'redux/store';
import { useSelector } from 'react-redux';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { getCanLinkCollectionLesson } from 'redux/actions/collections';

// hooks
import { useLecturePageFromParams, useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { useCreateLesson, useHideAddUIOnOpen } from 'lecture_pages/hooks/lecture-page-edit-top-panel';

// styles
import { hexToRgbaString, warning, white } from 'styles/global_defaults/colors';
import { doubleSpacing, halfSpacing } from 'styles/global_defaults/scaffolding';

// components
import NvDropdown, { NvDropdownAlign, NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import { hideAddUI } from 'lecture_pages/templates/components/nv-add-component';
import { LecturePageMode } from 'lecture_pages/components';
import LinkCollectionLessonsModal from '../../../../courses/components/course-lesson-link/link-lessons-modal';
import { config } from '../../../../../config/config.json';


const AddNewLessonButton = () => {
  const styles = css`
    .add-new-lesson-icon {
      transform: rotate(180deg);
    }

    .edit-menu-icon-styles {
      width: ${doubleSpacing}px;
      height: ${doubleSpacing}px;
      border-radius: 50%;
      background-color: ${hexToRgbaString(white, 0.2)};

      &:hover {
        background-color: ${hexToRgbaString(white, 0.4)};
        cursor: pointer;
      }
    }

    .border-style {
      border: 2px solid ${warning};
    }

    .bs4-dropdown-menu {
      padding-top: ${halfSpacing}px !important;
      padding-bottom: ${halfSpacing}px !important;
    }
    .bs4-dropdown-item {
      padding-top: ${halfSpacing}px;
      padding-bottom: ${halfSpacing}px;
    }
  `;

  const params = useLecturePageParams();
  const dispatch = useAppDispatch();
  const lecturePage = useLecturePageFromParams();
  const createLesson = useCreateLesson();
  const hideAddUIOnOpen = useHideAddUIOnOpen();
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const currentCourse = useSelector(getCurrentCourse);
  const { canLinkCollectionLesson } = useSelector(state => state.app.collection);

  const [showAddFromLessonModal, setShowAddFromLessonModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (!currentCourse?.isContentManagementCollection) {
      /**
       * To display the 'Add from Lesson Library' option, check if the user has
       * any collection with at least one folder containing a lesson.
       */
      if (canLinkCollectionLesson === null) {
        dispatch(getCanLinkCollectionLesson());
      }
    }
  }, [
    dispatch, canLinkCollectionLesson,
    currentCourse?.isContentManagementCollection,
  ]);

  const addFromLessonLibrary = useCallback(() => {
    setShowAddFromLessonModal(true);
  }, []);

  const closeAddFromLessonModal = useCallback(() => {
    setShowAddFromLessonModal(false);
  }, []);

  const adminDropdownItems = useMemo<NvDropdownOption[]>(() => {
    const items: NvDropdownOption[] = [];

    items.push(
      {
        type: 'text',
        text: t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.ADD_BLANK_LESSON(courseAliases),
        callback: createLesson,
        dataQa: config.pendo.lectureEdit.editMenuTopPanel.createLessonOption,
      },

    );
    if (!currentCourse?.isContentManagementCollection && canLinkCollectionLesson) {
      items.push(
        {
          type: 'text',
          text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.ADD_FROM_LESSON_LIBRARY(),
          callback: addFromLessonLibrary,
          dataQa: config.pendo.lectureEdit.editMenuTopPanel.addFromLessonLibraryOption,
        },
      );
    }
    return items;
  }, [
    currentCourse?.isContentManagementCollection,
    canLinkCollectionLesson,
    addFromLessonLibrary,
    courseAliases,
    createLesson,
  ]);

  const handleToggle = useCallback(
    (showList: boolean) => {
      setIsDropdownOpen(showList);
      if (showList) {
        hideAddUIOnOpen();
      }
    },
    [hideAddUIOnOpen],
  );

  return (
    <div css={styles}>
      <NvDropdown
        buttonStyle={NvDropdownButtonStyle.ICON}
        offset={10}
        maxWidth={190}
        items={adminDropdownItems}
        onToggle={handleToggle}
        iconClass={`icon-new-note edit-menu-icon-styles icon-small
          d-flex text-white align-items-center justify-content-center mr-4 add-new-lesson-icon ${isDropdownOpen ? 'border-style' : ''}`}
        align={NvDropdownAlign.LEFT}
        toggleDataQa={config.pendo.lectureEdit.editMenuTopPanel.addNewLessonOptions}
        altLabel={t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.ADD_NEW_LESSON(courseAliases)}
        tooltip={t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.ADD_NEW_LESSON(courseAliases)}
        tooltipPlacement='bottom'
        focusOutOnItemClick
      />


      {showAddFromLessonModal && (
        <LinkCollectionLessonsModal
          show={showAddFromLessonModal}
          onClose={closeAddFromLessonModal}
          sectionId={lecturePage?.lectureSectionId}
          courseId={currentCourse.id}
        />
      )}
    </div>
  );
};

export default AddNewLessonButton;
